import { useState } from "react";
import { Link } from "react-router-dom";
import { Calculator, ArrowRight } from "lucide-react";

const ALIQUOTA_CONTRIBUTI = 0.36;
const ALIQUOTA_RITENUTA = 0.20;

function eur(v) {
  return v.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function CalcolatorePage() {
  const [lordo, setLordo] = useState(500);
  const lordoNum = Math.min(Math.max(Number(lordo) || 0, 0), 100000);

  const contributi = lordoNum * ALIQUOTA_CONTRIBUTI;
  const ritenuta = lordoNum * ALIQUOTA_RITENUTA;
  const netto = lordoNum - contributi - ritenuta;

  const risultati = [
    { testid: "calc-contributi", label: "Contributi (36%)", valore: contributi, nota: "Contributi previdenziali sul compenso lordo" },
    { testid: "calc-ritenuta", label: "Ritenuta d'acconto (20%)", valore: ritenuta, nota: "Tassa trattenuta sul compenso lordo" },
    { testid: "calc-netto", label: "Netto stimato in tasca", valore: netto, nota: "Lordo meno contributi e ritenuta d'acconto", highlight: true },
  ];

  return (
    <div className="relative overflow-hidden bg-[#3A0E1B] px-4 py-16 sm:px-6 lg:px-8" data-testid="calcolatore-page">
      <div className="pointer-events-none absolute inset-0 grain" aria-hidden="true" />
      <div className="relative z-10 mx-auto max-w-5xl">
        <div className="flex items-center gap-3">
          <span className="flex h-12 w-12 items-center justify-center rounded-lg border border-gold/40 bg-[#632035] text-gold">
            <Calculator className="h-6 w-6" />
          </span>
          <p className="kicker">Strumento gratuito</p>
        </div>
        <h1 className="mt-4 font-heading text-3xl font-bold tracking-tight text-[#FFFDF7] sm:text-4xl lg:text-5xl" data-testid="calcolatore-title">
          Calcolatore <span className="italic text-gold">contributi</span>
        </h1>
        <p className="mt-3 max-w-2xl text-[#B5A797]">
          Inserisci il compenso lordo della tua esibizione e scopri subito quanto va in contributi e quanto resta in tasca a te.
        </p>

        <div className="mt-10 grid gap-8 lg:grid-cols-5">
          <div className="rounded-xl border border-gold/30 bg-[#4A1424] p-8 shadow-xl ring-1 ring-inset ring-gold/10 lg:col-span-2">
            <label className="text-xs font-semibold uppercase tracking-[0.2em] text-gold" htmlFor="lordo-input">
              Compenso lordo
            </label>
            <div className="mt-3 flex items-baseline gap-2">
              <input
                id="lordo-input"
                type="number"
                min="0"
                max="100000"
                step="10"
                value={lordo}
                onChange={(e) => setLordo(e.target.value)}
                data-testid="calcolatore-input-lordo"
                className="w-full rounded-lg border border-gold/40 bg-[#3A0E1B] px-4 py-3 font-heading text-3xl font-bold text-[#FFFDF7] outline-none transition-colors focus:border-gold"
              />
              <span className="font-heading text-2xl font-bold text-gold">€</span>
            </div>
            <input
              type="range"
              min="0"
              max="5000"
              step="25"
              value={Math.min(lordoNum, 5000)}
              onChange={(e) => setLordo(Number(e.target.value))}
              data-testid="calcolatore-slider"
              className="mt-6 w-full accent-[#D4AF37]"
              aria-label="Compenso lordo (slider)"
            />
            <div className="mt-1 flex justify-between text-xs text-[#B5A797]">
              <span>0 €</span>
              <span>5.000 €</span>
            </div>
            <p className="mt-6 text-xs leading-relaxed text-[#B5A797]" data-testid="calcolatore-disclaimer">
              Stima indicativa: contributi 36% e ritenuta d'acconto 20% calcolati sul compenso lordo.
              Il calcolo esatto dipende dalla tua posizione specifica: per un conto preciso contattaci.
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-3 lg:col-span-3" data-testid="calcolatore-risultati">
            {risultati.map((r) => (
              <div
                key={r.testid}
                className={`rounded-xl border p-6 shadow-xl ring-1 ring-inset transition-[border-color,transform,box-shadow] duration-300 hover:-translate-y-1 ${
                  r.highlight
                    ? "border-gold/60 bg-[#571C2E] ring-gold/25 hover:shadow-[0_16px_40px_rgba(212,175,55,0.15)]"
                    : "border-gold/30 bg-[#4A1424] ring-gold/10 hover:border-gold/50"
                }`}
                data-testid={r.testid}
              >
                <p className="text-xs font-semibold uppercase tracking-widest text-[#E8DED1]">{r.label}</p>
                <p className="mt-3 font-heading text-4xl font-bold text-gold">
                  {eur(r.valore)}<span className="text-xl"> €</span>
                </p>
                <p className="mt-2 text-xs leading-relaxed text-[#B5A797]">{r.nota}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-10 rounded-xl border border-gold/30 bg-[#571C2E] p-6 text-center ring-1 ring-inset ring-gold/10 sm:p-8" data-testid="calcolatore-cta">
          <p className="font-heading text-xl font-semibold text-[#FFFDF7]">Pronto a metterti in regola?</p>
          <p className="mt-2 text-sm text-[#B5A797]">Richiedi il certificato di agibilità per la tua prossima esibizione: ci pensiamo noi.</p>
          <Link to="/richiedi-agibilita" data-testid="calcolatore-cta-button" className="mt-4 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-gold to-[#F7E5A9] px-8 py-3 text-sm font-bold uppercase tracking-wider text-[#1F040A] shadow-[0_4px_20px_rgba(212,175,55,0.35)] transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_8px_28px_rgba(212,175,55,0.45)]">
            RICHIEDI AGIBILITÀ <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
